import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, Users, Trash2, Coins, KeyRound, Copy, Plus, Minus } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditLedgerDialog } from "@/components/admin/CreditLedgerDialog";

const detail = (e, fallback) => {
  const d = e.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

const CreateSubUserForm = ({ onCreated }) => {
  const [form, setForm] = useState({ name: "", email: "", password: genPassword(), credits: 10, report_email: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || form.password.length < 8) { toast.error("Email and a password of 8+ characters are required."); return; }
    setBusy(true);
    try {
      const res = await api.post("/admin/sub-users", { ...form, credits: Number(form.credits) || 0, base_url: window.location.origin });
      toast.success(`Sub-user ${res.data.email} created with ${res.data.credits} credits`);
      if (res.data.invite_sent) toast.success(`Invite email with login details sent to ${res.data.email}`, { duration: 6000 });
      else toast.warning(`Invite email not sent${res.data.invite_error ? `: ${res.data.invite_error}` : ""}. Login details copied to clipboard instead.`, { duration: 9000 });
      try {
        await navigator.clipboard.writeText(`Login: ${window.location.origin}/admin-login\nEmail: ${form.email.trim()}\nPassword: ${form.password}`);
      } catch (err) { /* clipboard unavailable */ }
      setForm({ name: "", email: "", password: genPassword(), credits: 10, report_email: "" });
      onCreated();
    } catch (err) { toast.error(detail(err, "Could not create sub-user")); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="create-subuser-form">
      <h2 className="font-heading font-bold text-lg text-slate-900 mb-1">Add a sub-user</h2>
      <p className="font-body text-sm text-slate-500 mb-4">Sub-users sign in at <span className="font-mono">/admin-login</span> and can only generate as many codes as the credits you assign. Login details are emailed to them automatically.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
        <div>
          <Label className="font-heading font-bold text-slate-800">Name</Label>
          <Input value={form.name} onChange={set("name")} placeholder="Priya Sharma" className="mt-1.5 rounded-xl" data-testid="su-name-input" />
        </div>
        <div>
          <Label className="font-heading font-bold text-slate-800">Login email</Label>
          <Input type="email" value={form.email} onChange={set("email")} placeholder="priya@partner.com" className="mt-1.5 rounded-xl" data-testid="su-email-input" />
        </div>
        <div>
          <Label className="font-heading font-bold text-slate-800">Initial password</Label>
          <div className="flex gap-1.5 mt-1.5">
            <Input value={form.password} onChange={set("password")} className="rounded-xl font-mono" data-testid="su-password-input" />
            <Button type="button" variant="outline" className="rounded-xl shrink-0" title="Generate"
              onClick={() => setForm((f) => ({ ...f, password: genPassword() }))} data-testid="su-gen-password-btn">
              <KeyRound className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div>
          <Label className="font-heading font-bold text-slate-800">Credits</Label>
          <Input type="number" min="0" value={form.credits} onChange={set("credits")} className="mt-1.5 rounded-xl" data-testid="su-credits-input" />
        </div>
        <div>
          <Label className="font-heading font-bold text-slate-800">Report email (optional)</Label>
          <Input type="email" value={form.report_email} onChange={set("report_email")} placeholder="reports@partner.com" className="mt-1.5 rounded-xl" data-testid="su-report-email-input" />
        </div>
        <Button type="submit" disabled={busy} className="rounded-xl h-11 font-heading font-bold" data-testid="create-subuser-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />} Create
        </Button>
      </div>
    </form>
  );
};

const CreditsControl = ({ u, onChanged }) => {
  const [amt, setAmt] = useState(5);
  const [busy, setBusy] = useState(false);
  const adjust = async (sign) => {
    const n = Math.abs(Number(amt) || 0);
    if (!n) { toast.error("Enter an amount."); return; }
    setBusy(true);
    try {
      await api.put(`/admin/sub-users/${u.user_id}/credits`, { delta: sign * n });
      toast.success(sign > 0 ? `Added ${n} credits to ${u.name}` : `Took back ${n} credits from ${u.name}`);
      onChanged();
    } catch (e) { toast.error(detail(e, "Could not adjust credits")); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="outline" className="rounded-lg h-8 w-8 p-0 text-rose-600" disabled={busy || u.credits === 0}
        title="Take back credits" onClick={() => adjust(-1)} data-testid={`credits-minus-${u.email}`}>
        <Minus className="w-3.5 h-3.5" />
      </Button>
      <Input type="number" min="1" value={amt} onChange={(e) => setAmt(e.target.value)}
        className="h-8 w-16 rounded-lg text-center px-1" data-testid={`credits-amount-${u.email}`} />
      <Button size="sm" variant="outline" className="rounded-lg h-8 w-8 p-0 text-emerald-600" disabled={busy}
        title="Add credits" onClick={() => adjust(1)} data-testid={`credits-plus-${u.email}`}>
        <Plus className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
};

const SubUserRow = ({ u, onChanged }) => {
  const resetPwd = async () => {
    const pwd = window.prompt(`New password for ${u.email} (min 8 characters):`, genPassword());
    if (!pwd) return;
    try {
      const res = await api.put(`/admin/sub-users/${u.user_id}/password`, { password: pwd, base_url: window.location.origin });
      try { await navigator.clipboard.writeText(`Email: ${u.email}\nPassword: ${pwd}`); } catch (e) { /* ignore */ }
      toast.success(res.data.email_sent ? `Password reset and emailed to ${u.email}. They've been signed out.` : "Password reset and copied to clipboard. The sub-user has been signed out.");
      if (!res.data.email_sent && res.data.email_error) toast.warning(`Email not sent: ${res.data.email_error}`, { duration: 8000 });
    } catch (e) { toast.error(detail(e, "Could not reset password")); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete sub-user ${u.email}? Their codes stay in your global list, but they lose access immediately.`)) return;
    try { await api.delete(`/admin/sub-users/${u.user_id}`); toast.success("Sub-user deleted"); onChanged(); }
    catch (e) { toast.error(detail(e, "Delete failed")); }
  };
  const copyLogin = async () => {
    try { await navigator.clipboard.writeText(`Login: ${window.location.origin}/admin-login\nEmail: ${u.email}`); toast.success("Login link + email copied"); }
    catch (e) { window.prompt("Copy:", `${window.location.origin}/admin-login`); }
  };
  return (
    <tr className="font-body text-sm" data-testid={`subuser-row-${u.email}`}>
      <td className="px-5 py-3">
        <p className="font-heading font-bold text-slate-900">{u.name}</p>
        <p className="text-xs text-slate-500">{u.email}</p>
      </td>
      <td className="px-5 py-3 text-slate-500 max-w-[180px] truncate">{u.report_email || "—"}</td>
      <td className="px-5 py-3">
        <span className={`font-heading font-extrabold text-lg ${u.credits === 0 ? "text-rose-600" : "text-slate-900"}`} data-testid={`credits-${u.email}`}>{u.credits}</span>
        <span className="text-xs text-slate-400 ml-1">left</span>
      </td>
      <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{u.credits_used} used · {u.codes_completed} done</td>
      <td className="px-5 py-3"><CreditsControl u={u} onChanged={onChanged} /></td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end gap-1.5">
          <CreditLedgerDialog userId={u.user_id} name={u.email} compact />
          <Button size="sm" variant="outline" className="rounded-lg h-8" title="Copy login link" onClick={copyLogin} data-testid={`copy-login-${u.email}`}><Copy className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="outline" className="rounded-lg h-8" title="Reset password" onClick={resetPwd} data-testid={`reset-pwd-${u.email}`}><KeyRound className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="ghost" className="rounded-lg h-8 text-destructive hover:text-destructive" title="Delete sub-user" onClick={remove} data-testid={`delete-subuser-${u.email}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </td>
    </tr>
  );
};

export const SubUsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(true);
  const load = async () => {
    try { const res = await api.get("/admin/sub-users"); setUsers(res.data); }
    catch (e) { toast.error("Could not load sub-users"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const totals = users.reduce((a, u) => ({ credits: a.credits + u.credits, used: a.used + u.credits_used, done: a.done + u.codes_completed }), { credits: 0, used: 0, done: 0 });

  return (
    <div className="space-y-8" data-testid="subusers-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[["Sub-users", users.length, Users], ["Credits outstanding", totals.credits, Coins], ["Credits used", totals.used, Coins], ["Reports completed", totals.done, Users]].map(([k, v, Icon]) => (
          <div key={k} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`su-stat-${k.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <p className="font-heading font-extrabold text-2xl text-slate-900">{v}</p>
              <Icon className="w-4 h-4 text-slate-300" />
            </div>
            <p className="font-body text-xs text-slate-500">{k}</p>
          </div>
        ))}
      </div>

      <CreateSubUserForm onCreated={load} />

      {busy ? <p className="font-body text-slate-400">Loading…</p> : users.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center" data-testid="no-subusers">
          <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-body text-slate-500">No sub-users yet. Add your first partner above.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="font-heading text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-3">Sub-user</th>
                  <th className="px-5 py-3">Report email</th>
                  <th className="px-5 py-3">Credits</th>
                  <th className="px-5 py-3">Usage</th>
                  <th className="px-5 py-3">Adjust credits</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => <SubUserRow key={u.user_id} u={u} onChanged={load} />)}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
